// Marker.io feedback widget.
//
// Gated on `public.markerProjectId`, which is empty everywhere except the
// staging deploy (see .github/workflows/deploy-staging.yml). Production and
// local dev therefore never load it — same on/off shape as the PostHog key in
// 06.posthog.client.ts. To exercise it locally, set
// NUXT_PUBLIC_MARKER_PROJECT_ID in src/nuxt/.env.
//
// This is a de-minified transcription of Marker.io's install snippet: seed a
// stub `window.Marker` that queues any API calls made before the real widget
// arrives, then async-load the shim that replaces it. We call no Marker.*
// methods today, but the stub is what makes doing so safe at any point after
// this plugin runs.

type MarkerStubMethod = (...args: unknown[]) => void;

interface MarkerStub extends Record<string, MarkerStubMethod> {
    __cs: unknown[][];
}

declare global {
    interface Window {
        markerConfig?: { project: string; source: string };
        __Marker?: Record<string, unknown>;
        Marker?: MarkerStub;
    }
}

const STUB_METHODS = [
    'show',
    'hide',
    'isVisible',
    'capture',
    'cancelCapture',
    'unload',
    'reload',
    'isExtensionInstalled',
    'setReporter',
    'clearReporter',
    'setCustomData',
    'on',
    'off',
] as const;

export default defineNuxtPlugin(() => {
    const project = useRuntimeConfig().public.markerProjectId;
    if (!project) return;

    // The shim sets window.__Marker once it has taken over; bail if something
    // (an HMR re-run, a second plugin registration) already installed it.
    if (window.__Marker) return;
    window.__Marker = {};

    window.markerConfig = { project, source: 'snippet' };

    const queue: unknown[][] = [];
    const stub = { __cs: queue } as MarkerStub;
    for (const method of STUB_METHODS) {
        stub[method] = (...args: unknown[]) => queue.push([method, ...args]);
    }
    window.Marker = stub;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://edge.marker.io/latest/shim.js';
    document.head.appendChild(script);
});
